import React from 'react';
import clsx from 'clsx';
import {inputBaseClasses} from '@mui/material/InputBase';
import TextField, {type TextFieldProps} from '@mui/material/TextField';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {useSharedRef} from '@netx/core-components/hooks/useSharedRef';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import {i18n} from '@netx/core/i18n';
import {
	getSearchFilterUtilityClass,
	searchFilterClasses,
	type SearchFilterClasses,
} from './searchFilterClasses';
import {
	SearchFilterEndAdornment,
	type SearchFilterEndAdornmentProps,
} from './SearchFilterEndAdornment';
import {
	SearchFilterStartAdornment,
	type SearchFilterStartAdornmentProps,
} from './SearchFilterStartAdornment';
import {searchFilterStartAdornmentClasses} from './searchFilterStartAdornmentClasses';

const useUtilityClasses = (ownerState: any) => {
	const {classes, hideStartAdornment, variant} = ownerState;
	const slots = {
		root: [
			'root',
			hideStartAdornment && 'hideStartAdornment',
			variant === 'rounded' && 'rounded',
		],
	};
	return composeClasses(slots, getSearchFilterUtilityClass, classes);
};

export const SearchFilterRoot = styled(TextField, {
	name: 'SearchFilter',
	slot: 'Root',
})(({theme, ownerState}) => {
	const {hideStartAdornment} = ownerState;
	return {
		[`& .${inputBaseClasses.input}`]: {
			height: 40,
			padding: theme.spacing(0, 4, 0, hideStartAdornment ? 2 : 0),
		},
		[`& .${inputBaseClasses.root}`]: {
			[`&.${inputBaseClasses.disabled}`]: {
				[`& .${searchFilterStartAdornmentClasses.root}`]: {
					opacity: 0.5,
				},
			},
		},
		[`&.${searchFilterClasses.rounded}`]: {
			[`& .${inputBaseClasses.root}`]: {
				borderRadius: 40,
				'&::after, &::before': {
					display: 'none',
				},
			},
		},
	};
});

export interface SearchFilterProps extends WithClasses<
	SearchFilterClasses,
	Omit<
		TextFieldProps,
		'classes' | 'hiddenLabel' | 'label' | 'onChange' | 'value' | 'variant'
	>
> {
	filterStr?: string;
	hideStartAdornment?: boolean;
	variant?: Required<TextFieldProps>['variant'] | 'rounded';
	onFilter?: (filterStr: string) => void;
	SearchFilterEndAdornmentProps?: Partial<SearchFilterEndAdornmentProps>;
	SearchFilterStartAdornmentProps?: Partial<SearchFilterStartAdornmentProps>;
}

export const SearchFilterPrimitive = React.forwardRef<
	HTMLDivElement,
	SearchFilterProps
>(
	(
		{
			className,
			disabled,
			filterStr = '',
			onFilter,
			hideStartAdornment,
			inputRef,
			variant = 'standard',
			SearchFilterEndAdornmentProps,
			SearchFilterStartAdornmentProps,
			...props
		}: SearchFilterProps,
		ref,
	) => {
		const ownerState = {...props, variant, hideStartAdornment};
		const classes = useUtilityClasses(ownerState);
		const innerInputRef = useSharedRef(null, [inputRef]);

		const handleChange = useConstCallback(
			(event: React.ChangeEvent<HTMLInputElement>) => {
				// TODO: now that we are on v5 this may no longer be neccesary
				// this was added to avoid false change events from initial render
				if (!event) return;
				onFilter?.(event.target.value);
			},
		);

		const handleClear = useConstCallback(() => {
			onFilter?.('');
			innerInputRef.current?.focus();
		});

		return (
			<SearchFilterRoot
				ref={ref}
				className={clsx(classes.root, className)}
				disabled={disabled}
				ownerState={ownerState}
				placeholder={i18n.t('i18n.filter')}
				variant={variant === 'rounded' ? 'filled' : (variant ?? 'standard')}
				{...props}
				onChange={handleChange}
				inputRef={innerInputRef}
				value={filterStr}
				InputProps={{
					inputRef: innerInputRef,
					endAdornment:
						filterStr !== '' ? (
							<SearchFilterEndAdornment
								disabled={disabled}
								onClick={handleClear}
								{...SearchFilterEndAdornmentProps}
							/>
						) : undefined,
					startAdornment: !hideStartAdornment ? (
						<SearchFilterStartAdornment {...SearchFilterStartAdornmentProps} />
					) : undefined,
					...props.InputProps,
				}}
			/>
		);
	},
);

export const SearchFilter = React.memo(SearchFilterPrimitive);
export default SearchFilter;
